import jp from 'jsonpath';
import type {
  AnnotatedJsonSchema,
  AssetType,
  ConfigEntityDetail,
  EntityAssetAnnotation,
  IdEntityReferenceAnnotation,
  RelationshipEntityReferenceAnnotation,
} from '@web-config-app/core';
import { ASSET_TYPE_TO_ENTITY_TYPE, URI_FORMATS } from '@web-config-app/core';
import {
  findSchemaPathsWhere,
  convertSchemaPathToJsonPath,
  getResolvedSchemaNodeAtPath,
  isRelationshipEntityReference,
} from '@web-config-app/schema-utils';

const isLinkEntityReference = (
  reference:
    | IdEntityReferenceAnnotation
    | RelationshipEntityReferenceAnnotation
    | undefined,
): reference is IdEntityReferenceAnnotation =>
  Boolean(reference && 'domainEntityType' in reference);

const isMediaAssetReference = (
  annotation: EntityAssetAnnotation | undefined,
  schemaNode: AnnotatedJsonSchema | undefined,
): annotation is EntityAssetAnnotation =>
  annotation !== undefined &&
  'type' in annotation &&
  Boolean(ASSET_TYPE_TO_ENTITY_TYPE[annotation.type as AssetType]) &&
  !URI_FORMATS.includes(schemaNode?.format ?? '');

/**
 * Represents a linked entity reference found in entity data
 */
export interface LinkedEntityReference {
  /**
   * The domain entity ID being referenced
   */
  domainEntityId: string;
  /**
   * The domain entity type of the referenced entity
   * (e.g., 'config-image', 'config-pdf')
   */
  domainEntityType: string;
  /**
   * The JSONPath to this value in the entity data (e.g., '$.attributes.domainEntityAttributes.activityId')
   */
  jsonPath: string;
}

/**
 * Represents a relationship entity reference found in entity data
 */
export interface RelationshipEntityReference {
  /**
   * The relationship name from the x-entity-reference annotation
   */
  relationshipName: string;
  /**
   * The JSONPath to this value in the entity data (e.g., '$.attributes.domainEntityAttributes.activityId')
   */
  jsonPath: string;
  /**
   * The config entity ID being referenced
   */
  configEntityId: string;
}

export type EntityReference =
  | LinkedEntityReference
  | RelationshipEntityReference;

/**
 * Type guard to check if a reference is a linked entity reference
 */
export const isLinkedEntityRef = (
  ref: EntityReference,
): ref is LinkedEntityReference => 'domainEntityId' in ref;

/**
 * Type guard to check if a reference is a relationship entity reference
 */
export const isRelationshipEntityRef = (
  ref: EntityReference,
): ref is RelationshipEntityReference => 'relationshipName' in ref;

/**
 * Extracts entity reference IDs from an entity based on its schema.
 * This function traverses through an entity's schema to find entity references
 * and retrieves their corresponding IDs from the entity data.
 *
 * Supported reference types:
 * - Linked entity references (marked with x-entity-reference.domainEntityType)
 * - Relationship references (marked with x-entity-reference.relationship)
 * - Media asset references (marked with x-entity-asset)
 *
 * @param entityData - The partial entity data containing the actual values
 * @param schema - The schema definition describing the structure of the entity
 * @returns An array of {@link EntityReference} objects (LinkedEntityReference or RelationshipEntityReference)
 *
 * @example
 * ```typescript
 * const schema = {
 *   properties: {
 *     relatedProductId: {
 *       type: 'string',
 *       'x-entity-reference': { domainEntityType: 'config-activity' }
 *     },
 *     activityId: {
 *       type: 'string',
 *       'x-entity-reference': { relationship: 'activity' }
 *     }
 *   }
 * };
 * const entityData = {
 *   attributes: {
 *     domainEntityAttributes: {
 *       relatedProductId: 'product-123',
 *       activityId: 'activity-456'
 *     }
 *   }
 * };
 *
 * const allRefs = getEntityReferenceIds(entityData, schema);
 *
 * const linkedRefs = allRefs.filter(isLinkedEntityRef);
 * // Returns: [{ domainEntityId: 'product-123', domainEntityType: 'config-activity', jsonPath: '$.attributes.domainEntityAttributes.relatedProductId' }]
 *
 * const relationshipRefs = allRefs.filter(isRelationshipEntityRef);
 * // Returns: [{ relationshipName: 'activity', entityId: 'activity-456', jsonPath: '$.attributes.domainEntityAttributes.activityId' }]
 * ```
 */
export const getEntityReferenceIds = (
  entityData: Partial<ConfigEntityDetail> | undefined,
  schema: AnnotatedJsonSchema,
): EntityReference[] => {
  if (!entityData) {
    return [];
  }

  /**
   * Find all schema paths that have entity references
   * This will find ALL paths across all combinator branches (oneOf, anyOf, allOf)
   * - x-entity-reference.domainEntityType (link references)
   * - x-entity-reference.relationship (relationship references)
   * - x-entity-asset (media asset references)
   */
  const foundRefSchemaPaths = findSchemaPathsWhere(
    schema,
    (schemaNode) =>
      isLinkEntityReference(schemaNode['x-entity-reference']) ||
      isRelationshipEntityReference(schemaNode['x-entity-reference']) ||
      isMediaAssetReference(schemaNode['x-entity-asset'], schemaNode),
  );

  const references: EntityReference[] = [];

  foundRefSchemaPaths.forEach((schemaPath) => {
    const jsonPath = convertSchemaPathToJsonPath(schemaPath);

    // Get all data nodes that match this path
    const nodes = jp.nodes(entityData, jsonPath);

    nodes.forEach(({ path: nodePath, value }) => {
      // Convert JSONPath array to dot notation for schema resolution
      // nodePath is like ['$', 'attributes', 'domainEntityAttributes', 'relatedProductId']
      const dataPath = nodePath.slice(1).join('.');
      // Full JSONPath for callers to use with jp.value()
      const nodeJsonPath = `$.${dataPath}`;

      // Resolve the schema node for this specific data item
      // This handles oneOf/anyOf/allOf discriminators by looking at the actual data
      const resolvedSchemaNode = getResolvedSchemaNodeAtPath(
        dataPath,
        schema,
        entityData,
      );

      if (!resolvedSchemaNode) {
        return;
      }

      const entityReference = resolvedSchemaNode['x-entity-reference'];
      const entityAsset = resolvedSchemaNode['x-entity-asset'];

      // When the annotation has `relationshipDataIdKey`, the value is an
      // object (e.g. { id: "some-id", type: "config-campaign" }) and we
      // need to extract the ID using that key.
      if (
        entityReference &&
        isRelationshipEntityReference(entityReference) &&
        typeof value === 'object' &&
        value !== null &&
        'relationshipDataIdKey' in entityReference
      ) {
        const idKey = (entityReference as RelationshipEntityReferenceAnnotation)
          .relationshipDataIdKey;
        if (typeof idKey === 'string' && idKey.length > 0) {
          const extractedId = (value as Record<string, unknown>)[idKey];
          if (typeof extractedId === 'string' && extractedId.length > 0) {
            references.push({
              relationshipName: entityReference.relationship,
              jsonPath: `${nodeJsonPath}.${idKey}`,
              configEntityId: extractedId,
            });
          }
          return;
        }
      }

      // Skip non-string values for all other reference types
      if (typeof value !== 'string' || value.length === 0) {
        return;
      }

      if (isLinkEntityReference(entityReference)) {
        references.push({
          domainEntityId: value,
          domainEntityType: entityReference.domainEntityType,
          jsonPath: nodeJsonPath,
        });
      } else if (
        entityReference &&
        isRelationshipEntityReference(entityReference)
      ) {
        references.push({
          relationshipName: entityReference.relationship,
          jsonPath: nodeJsonPath,
          configEntityId: value,
        });
      } else if (isMediaAssetReference(entityAsset, resolvedSchemaNode)) {
        // Map asset type to entity type (e.g., 'image' -> 'config-image')
        // Only include assets that have a corresponding entity type mapping
        const domainEntityType =
          ASSET_TYPE_TO_ENTITY_TYPE[entityAsset.type as AssetType];
        if (domainEntityType) {
          references.push({
            domainEntityId: value,
            domainEntityType,
            jsonPath: nodeJsonPath,
          });
        }
      }
    });
  });

  return references;
};
